import Axios from "axios";
import { Fzf } from "fzf";
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import KuikaLoader from "../../../../ui-kit/kuika-loader/kuika-loader";
import KuikaOverflowText from "../../../../ui-kit/kuika-overflow-text/kuika-overflow-text";
import { OVERFLOW_REVEAL_HOST_CLASS } from "../../../../ui-kit/kuika-overflow-text/overflow-reveal-controller";
import { MCP_CHAT_API_URL } from "../shared/mcp-chat-config";

export interface ModelOverrides {
  complex?: string;
  default?: string;
  cheap?: string;
  braid?: string;
}

interface OpenRouterModelInfo {
  id: string;
  name: string;
}

type TierKey = "complex" | "default" | "cheap" | "braid";

const TIER_LABELS: Record<TierKey, string> = {
  complex: "Complex",
  default: "Default",
  cheap: "Cheap",
  braid: "Reasoning Engine"
};

const TIER_DESCRIPTIONS: Record<TierKey, string> = {
  complex: "Multi-domain, vision, builds",
  default: "Everyday requests",
  cheap: "Ask mode / Q&A",
  braid: "Intent graphs, app planner, design tokens"
};

const MODEL_OVERRIDES_STORAGE_KEY = "kuika_mcp_model_overrides";

function loadStoredOverrides(): ModelOverrides {
  try {
    const stored = localStorage.getItem(MODEL_OVERRIDES_STORAGE_KEY);
    if (stored) return JSON.parse(stored);
  } catch {
    // ignore
  }
  return {};
}

function saveOverrides(overrides: ModelOverrides): void {
  try {
    const cleaned: ModelOverrides = {};
    if (overrides.complex) cleaned.complex = overrides.complex;
    if (overrides.default) cleaned.default = overrides.default;
    if (overrides.cheap) cleaned.cheap = overrides.cheap;
    if (overrides.braid) cleaned.braid = overrides.braid;
    if (Object.keys(cleaned).length > 0) {
      localStorage.setItem(MODEL_OVERRIDES_STORAGE_KEY, JSON.stringify(cleaned));
    } else {
      localStorage.removeItem(MODEL_OVERRIDES_STORAGE_KEY);
    }
  } catch {
    // ignore
  }
}

/** Returns the current model overrides from localStorage (for use outside React). */
export function getStoredModelOverrides(): ModelOverrides {
  return loadStoredOverrides();
}

interface McpChatModelSelectorProps {
  isOpen: boolean;
  onToggle: () => void;
  onOverridesChange?: (overrides: ModelOverrides) => void;
}

const McpChatModelSelector: React.FC<McpChatModelSelectorProps> = ({ isOpen, onToggle, onOverridesChange }) => {
  const [models, setModels] = useState<OpenRouterModelInfo[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [overrides, setOverrides] = useState<ModelOverrides>(loadStoredOverrides);
  const [searchTerms, setSearchTerms] = useState<Record<TierKey, string>>({
    complex: "",
    default: "",
    cheap: "",
    braid: ""
  });
  const popoverRef = useRef<HTMLDivElement>(null);
  const hasFetched = useRef(false);

  // Fetch models on first open
  useEffect(() => {
    if (!isOpen || hasFetched.current) return;
    hasFetched.current = true;
    setIsLoading(true);
    const load = async () => {
      try {
        const res = await Axios.get<{ models: OpenRouterModelInfo[] }>(`${MCP_CHAT_API_URL}/models`);
        setModels(res.data.models || []);
      } catch {
        // silent fail — models list will be empty
      } finally {
        setIsLoading(false);
      }
    };
    void load();
  }, [isOpen]);

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        onToggle();
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [isOpen, onToggle]);

  const handleSelect = useCallback(
    (tier: TierKey, modelId: string) => {
      const next = { ...overrides };
      if (modelId === "") {
        delete next[tier];
      } else {
        next[tier] = modelId;
      }
      setOverrides(next);
      saveOverrides(next);
      onOverridesChange?.(next);
    },
    [overrides, onOverridesChange]
  );

  const handleClearAll = useCallback(() => {
    const next: ModelOverrides = {};
    setOverrides(next);
    saveOverrides(next);
    setSearchTerms({ complex: "", default: "", cheap: "", braid: "" });
    onOverridesChange?.(next);
  }, [onOverridesChange]);

  const handleSearchChange = useCallback((tier: TierKey, value: string) => {
    setSearchTerms((prev) => ({ ...prev, [tier]: value }));
  }, []);

  const hasAnyOverride = !!(overrides.complex || overrides.default || overrides.cheap || overrides.braid);

  if (!isOpen) return null;

  return (
    <div className="mcp-model-selector" ref={popoverRef}>
      <div className="mcp-model-selector__header">
        <span className="mcp-model-selector__title">Model Overrides</span>
        {hasAnyOverride && (
          <button className="mcp-model-selector__clear-btn" onClick={handleClearAll} type="button">
            Reset All
          </button>
        )}
      </div>
      <div className="mcp-model-selector__tiers">
        {(["complex", "default", "cheap", "braid"] as TierKey[]).map((tier) => (
          <TierSelect
            key={tier}
            tier={tier}
            models={models}
            isLoading={isLoading}
            selectedModel={overrides[tier] || ""}
            searchTerm={searchTerms[tier]}
            onSearchChange={handleSearchChange}
            onSelect={handleSelect}
          />
        ))}
      </div>
      <div className="mcp-model-selector__footer">Models via OpenRouter. Leave empty for server defaults.</div>
    </div>
  );
};

interface TierSelectProps {
  tier: TierKey;
  models: OpenRouterModelInfo[];
  isLoading: boolean;
  selectedModel: string;
  searchTerm: string;
  onSearchChange: (tier: TierKey, value: string) => void;
  onSelect: (tier: TierKey, modelId: string) => void;
}

const TierSelect: React.FC<TierSelectProps> = memo(
  ({ tier, models, isLoading, selectedModel, searchTerm, onSearchChange, onSelect }) => {
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      if (!isDropdownOpen) return;
      const handleClick = (e: MouseEvent) => {
        if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
          setIsDropdownOpen(false);
        }
      };
      document.addEventListener("mousedown", handleClick);
      return () => document.removeEventListener("mousedown", handleClick);
    }, [isDropdownOpen]);

    const fzf = useMemo(
      () =>
        new Fzf(models, {
          selector: (m) => `${m.name} ${m.id}`,
          casing: "case-insensitive"
        }),
      [models]
    );

    const filteredModels = useMemo(() => {
      if (!searchTerm) return models;
      return fzf.find(searchTerm).map((r) => r.item);
    }, [fzf, models, searchTerm]);

    const selectedModelName = useMemo(() => {
      if (!selectedModel) return "";
      const found = models.find((m) => m.id === selectedModel);
      return found?.name || selectedModel;
    }, [models, selectedModel]);

    return (
      <div className="mcp-model-selector__tier" ref={containerRef}>
        <div className="mcp-model-selector__tier-label">
          <span className="mcp-model-selector__tier-name">{TIER_LABELS[tier]}</span>
          <span className="mcp-model-selector__tier-desc">{TIER_DESCRIPTIONS[tier]}</span>
        </div>
        <div className="mcp-model-selector__select-wrapper">
          <button
            className={`mcp-model-selector__select-btn ${OVERFLOW_REVEAL_HOST_CLASS}${selectedModel ? " mcp-model-selector__select-btn--active" : ""}`}
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            type="button"
          >
            <span className="mcp-model-selector__select-text">
              <KuikaOverflowText>{selectedModel ? selectedModelName : "Server default"}</KuikaOverflowText>
            </span>
            <svg width="10" height="10" viewBox="0 0 10 10" fill="currentColor">
              <path d="M2 3.5L5 6.5L8 3.5" stroke="currentColor" strokeWidth="1.2" fill="none" />
            </svg>
          </button>
          {isDropdownOpen && (
            <div className="mcp-model-selector__dropdown">
              <input
                className="mcp-model-selector__search"
                type="text"
                placeholder="Search models..."
                value={searchTerm}
                onChange={(e) => onSearchChange(tier, e.target.value)}
                autoFocus
              />
              <div className="mcp-model-selector__options">
                <button
                  className={`mcp-model-selector__option ${OVERFLOW_REVEAL_HOST_CLASS}${!selectedModel ? " mcp-model-selector__option--selected" : ""}`}
                  onClick={() => {
                    onSelect(tier, "");
                    setIsDropdownOpen(false);
                  }}
                  type="button"
                >
                  <span className="mcp-model-selector__option-name">
                    <KuikaOverflowText>Server default</KuikaOverflowText>
                  </span>
                </button>
                {/* A two-or-three-row list: the ring beside the words, not a skeleton. The row is the
                    announcement, so the ring itself stays out of the accessibility tree. */}
                {isLoading && (
                  <div className="mcp-model-selector__loading" role="status">
                    <KuikaLoader size="sm" decorative />
                    Loading models...
                  </div>
                )}
                {!isLoading && filteredModels.length === 0 && searchTerm && (
                  <div className="mcp-model-selector__loading">No models found</div>
                )}
                {filteredModels.map((m) => (
                  <button
                    key={m.id}
                    className={`mcp-model-selector__option ${OVERFLOW_REVEAL_HOST_CLASS}${selectedModel === m.id ? " mcp-model-selector__option--selected" : ""}`}
                    onClick={() => {
                      onSelect(tier, m.id);
                      setIsDropdownOpen(false);
                    }}
                    type="button"
                    title={m.id}
                  >
                    <span className="mcp-model-selector__option-name">
                      <KuikaOverflowText>{m.name}</KuikaOverflowText>
                    </span>
                    <span className="mcp-model-selector__option-id">
                      <KuikaOverflowText>{m.id}</KuikaOverflowText>
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
);

TierSelect.displayName = "TierSelect";

export default memo(McpChatModelSelector);
